import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { isPresentationProject } from "@utils/presentationMode";

import AmpliCurveChart from "./AmpliCurveChart";
import DisbursementHeader from "./DisbursementHeader";
import DisbursementPeriodFilter from "./DisbursementPeriodFilter";
import DisbursementViewSwitch from "./DisbursementViewSwitch";
import ResourceCatalog from "./ResourceCatalog";
import EapTable from "./eapTable/EapTable";
import GroupEditorOffcanvas from "./GroupEditorOffcanvas";
import RuleEditorOffcanvas, { normalizeTarget } from "./RuleEditorOffcanvas";
import OrganizeWithAiPanel from "./ai/OrganizeWithAiPanel";
import {
  fetchDisbursementRules,
  normalizeDisbursementList,
} from "./disbursementApi";

// 202 = o motor está refazendo a curva em segundo plano. A tela não fica esperando:
// avisa e relê uma vez depois desta janela, quando o estado novo já deve estar no ar.
const REGENERATION_RECHECK_MS = 2000;

// `?editRule=<chave do alvo>` é o link de volta do "Ajustar regra" do offcanvas do
// item (spec 8.4): a chave é a do ALVO da regra (`resource_group:erp:...`,
// `resource:...`, `node:...`), que é o que o motor publica em `contribution.ruleKey`.
export const EDIT_RULE_PARAM = "editRule";

// Chave de alvo -> alvo mínimo pro editor abrir. Só a chave chega pelo link, então o
// `kind` é derivado do prefixo dela (mesmo vocabulário de `engine.js#ruleKeys`) e o
// `level` é o de ARMAZENAMENTO — o nível de consulta é rederivado no fetch.
const targetFromRuleKey = (key) => {
  const text = String(key ?? "");
  if (text.startsWith("resource_group:")) {
    return {
      kind: "resource_group",
      key: text,
      level: "resource_group",
      label: text,
    };
  }
  if (text.startsWith("resource:")) {
    return { kind: "resource", key: text, level: "resource", label: text };
  }
  if (text.startsWith("node:")) {
    // O nível do nó não vem na chave; `substep` é o padrão do picker e o editor deixa
    // o cliente trocar antes de salvar.
    return {
      kind: "node",
      key: text,
      level: "substep",
      label: text.slice("node:".length),
    };
  }
  return null;
};

const Disbursement = () => {
  const { activeProject } = useProjectContext();
  const { selectProject } = useProjectActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const workId = activeProject?._id ?? null;
  const [notice, setNotice] = useState(null);
  // A EAP é a visão de entrada: é por ela que o cliente enxerga o orçamento inteiro e
  // decide onde cadastrar regra. "Insumos" é o recorte por família de insumo.
  const [filters, setFilters] = useState({ targetKind: "node" });
  const [items, setItems] = useState([]);
  const [listMetadata, setListMetadata] = useState(null);
  const [catalogSeed, setCatalogSeed] = useState(0);
  const [catalogClassification, setCatalogClassification] = useState("all");
  const [catalogReload, setCatalogReload] = useState(0);
  // "Uma resposta da rota já chegou (ou falhou) para esta obra": sem isso, no primeiro
  // render um `?editRule=` veria uma lista de regras vazia que nem foi pedida ainda e
  // abriria o editor pelo plano B — perdendo o perfil gravado da regra.
  const [listSettled, setListSettled] = useState(false);
  // `{ target, rule }` quando o editor está aberto; `null` fechado. O alvo entra já
  // NORMALIZADO (a identidade que o editor segue é a chave, não o objeto).
  const [editor, setEditor] = useState(null);
  // `{ group }` quando o editor de grupo Ampli está aberto (`group: null` = novo).
  const [groupEditor, setGroupEditor] = useState(null);
  // Painel "Organizar com IA" (Task 10): só existe montado, então o `false` do
  // botão nunca aparece pra quem está em modo apresentação (o botão que abre nem
  // renderiza sob `readOnly`).
  const [organizeOpen, setOrganizeOpen] = useState(false);
  // Guarda contra resposta desatualizada: cada chamada de `loadList` reserva o
  // próximo número de geração; se, quando o `await` volta, uma geração mais nova
  // já foi disparada (ex.: o usuário trocou de obra no meio do caminho), esta
  // resposta é descartada em vez de sobrescrever o estado com dado velho.
  const loadGeneration = useRef(0);
  const recheckTimer = useRef(null);

  // Timer de releitura do 202 nunca sobrevive à desmontagem nem à troca de obra:
  // `setState` depois disso seria atualização em componente morto.
  useEffect(() => () => clearTimeout(recheckTimer.current), []);

  useEffect(() => {
    setNotice(null);
    setEditor(null);
    setGroupEditor(null);
    setOrganizeOpen(false);
    clearTimeout(recheckTimer.current);
  }, [workId]);

  // A rota `GET .../disbursement/rules` não filtra por query (ver ruling FE6 em
  // `disbursementApi.js`): `filters` é aplicado aqui, client-side, sobre a lista
  // já normalizada, então uma mudança de filtro nunca precisa recarregar do servidor.
  const loadList = useCallback(async () => {
    loadGeneration.current += 1;
    const generation = loadGeneration.current;
    if (!workId) {
      setItems([]);
      setListSettled(false);
      return;
    }
    setListSettled(false);
    const payload = await fetchDisbursementRules(workId);
    if (generation !== loadGeneration.current) return;
    if (!payload) {
      setItems([]);
      setListMetadata(null);
      setListSettled(true);
      return;
    }
    const normalized = normalizeDisbursementList(payload);
    setItems(normalized.items);
    setListMetadata({
      workId,
      state: normalized.state
        ? { ...normalized.state, coverage: normalized.coverage }
        : null,
      resourceSummary: normalized.resourceSummary,
    });
    setCatalogReload((current) => current + 1);
    setListSettled(true);
  }, [workId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // A tabela da EAP precisa saber, por nó, se já existe regra própria — para o rótulo da
  // linha e para o editor abrir com a forma GRAVADA em vez do padrão à vista.
  const rulesByNodeKey = useMemo(
    () =>
      Object.fromEntries(
        items
          .filter(
            (item) =>
              item.kind === "rule" &&
              item.target?.kind === "node" &&
              item.target?.key,
          )
          .map((item) => [item.target.key, item]),
      ),
    [items],
  );

  // A regra gravada de cada linha, por `ruleId`: escolher no editor um alvo que já
  // tem regra tem que carregar a forma GRAVADA, não o padrão à vista (espec. 4.1).
  const rulesByRuleId = useMemo(
    () =>
      Object.fromEntries(
        items
          .filter((item) => item.kind === "rule" && item.ruleId)
          .map((item) => [item.ruleId, item]),
      ),
    [items],
  );

  // Chegada por `?editRule=`: abre o editor na regra pedida e LIMPA o parâmetro, pra
  // fechar o offcanvas e voltar não reabri-lo. Espera a lista carregar antes de
  // decidir — assim a linha gravada (com perfil e descrição) ganha do alvo mínimo
  // montado a partir da chave, que é só o plano B pra uma chave que não está na lista
  // (alvo herdado, regra inativa filtrada, obra sem cobertura ainda).
  const editRuleKey = searchParams.get(EDIT_RULE_PARAM);
  useEffect(() => {
    if (!editRuleKey || !listSettled) return;
    const row = items.find(
      (item) => item.kind === "rule" && item.target?.key === editRuleKey,
    );
    const seed = row
      ? {
          target: normalizeTarget({
            ...row.target,
            plannedCents: row.coveredCents,
          }),
          rule: row,
        }
      : { target: normalizeTarget(targetFromRuleKey(editRuleKey)), rule: null };
    if (!seed.target) return;
    setNotice(null);
    setEditor(seed);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(EDIT_RULE_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [editRuleKey, listSettled, items, setSearchParams]);

  if (!workId) return null;
  const readOnly = isPresentationProject(activeProject);

  const refresh = async (result, { recheck = false } = {}) => {
    const refreshed = await selectProject(workId);
    if (!refreshed) {
      // Recheque do 202 que não volta NÃO é "falha": a gravação deu certo e a curva
      // pode muito bem estar pronta. O aviso continua no estado neutro de
      // regeneração em vez de acusar um erro que não houve.
      if (!recheck) setNotice("falha");
      return;
    }
    if (result?.regeneration?.status === "regeneration_failed")
      setNotice("regraGravadaCurvaFalha");
    else if (result?.code === 202 || recheck) setNotice("regenerando");
    else setNotice("atualizada");
    await loadList();
    if (
      result?.code === 202 &&
      result?.regeneration?.status !== "regeneration_failed"
    ) {
      clearTimeout(recheckTimer.current);
      recheckTimer.current = setTimeout(() => {
        refresh({ code: 200 }, { recheck: true });
      }, REGENERATION_RECHECK_MS);
    }
  };

  // Depois de um 202 quem diz se a curva ficou pronta é o ESTADO do módulo na obra
  // recarregada, não o código HTTP da escrita: derivar no render mantém o aviso
  // honesto mesmo se o recheque chegar antes do motor terminar.
  const regenerationText =
    activeProject?.ampliDisbursement?.state === "ready"
      ? "Curva atualizada."
      : "Atualizando a curva…";

  const noticeText = {
    regenerando: regenerationText,
    atualizada: "Curva atualizada.",
    falha: "Não foi possível atualizar a página com a curva mais recente.",
    regraGravadaCurvaFalha:
      "A regra foi salva, mas a curva não pôde ser atualizada. Atualize a curva sem enviar a regra novamente.",
  }[notice];
  const noticeIsFailure = notice === "falha";

  return (
    <div className="ampli-disbursement-page">
      <DisbursementHeader
        moduleState={
          listMetadata?.workId === workId ? listMetadata.state : null
        }
        project={activeProject}
        readOnly={readOnly}
        onNewRule={() => {
          setNotice(null);
          setEditor({ target: null, rule: null });
        }}
        onNewGroup={() => {
          setNotice(null);
          setGroupEditor({ group: null });
        }}
        onOrganizeWithAi={() => {
          setNotice(null);
          setOrganizeOpen(true);
        }}
        onRegenerated={refresh}
      />
      {notice ? (
        <p
          className={`alert ${noticeIsFailure ? "alert-error" : "alert-info"}`}
          role={noticeIsFailure ? "alert" : "status"}
        >
          {noticeText}
        </p>
      ) : null}
      {listMetadata?.workId === workId &&
      listMetadata.resourceSummary?.eligibleUnclassified > 0 ? (
        <p role="status">
          {`${listMetadata.resourceSummary.eligibleUnclassified.toLocaleString(
            "pt-BR",
          )} insumos com previsto disponível estão sem classificação ERP. Eles continuam no catálogo; nenhum grupo foi inventado.`}
          <button
            type="button"
            onClick={() => {
              setCatalogClassification("unclassified");
              setCatalogSeed((current) => current + 1);
              setFilters({ targetKind: "resource" });
            }}
          >
            Ver insumos sem classificação
          </button>
        </p>
      ) : null}
      <DisbursementPeriodFilter />
      <AmpliCurveChart project={activeProject} />
      <DisbursementViewSwitch
        value={filters.targetKind}
        onChange={(targetKind) => {
          setCatalogClassification("all");
          setFilters({ targetKind });
        }}
      />
      {filters.targetKind === "resource" ? (
        <ResourceCatalog
          key={`${workId}:${catalogSeed}`}
          workId={workId}
          readOnly={readOnly}
          reloadKey={catalogReload}
          initialClassification={catalogClassification}
          rulesByRuleId={rulesByRuleId}
          onDefineRule={(target) => {
            if (readOnly || target.isEligible !== true) return;
            setEditor({
              target: normalizeTarget(target),
              rule: target.ruleId ? rulesByRuleId[target.ruleId] ?? null : null,
            });
          }}
        />
      ) : null}
      {filters.targetKind === "node" ? (
        // A key precisa ser ÚNICA entre os irmãos: `key={workId}` puro colidiria com a
        // do `RuleEditorOffcanvas` logo abaixo, e dois irmãos com a mesma key fazem o
        // React errar a reconciliação (os nós antigos ficam na árvore).
        <EapTable
          key={`eap-table-${workId}`}
          readOnly={readOnly}
          rulesByNodeKey={rulesByNodeKey}
          onEditRule={(target, rule) => {
            if (readOnly) return;
            setNotice(null);
            setEditor({ target: normalizeTarget(target), rule: rule ?? null });
          }}
        />
      ) : null}

      {/* `key` por obra: o editor guarda alvo, forma e descrição em estado local, e
          nada disso pode sobreviver a uma troca de obra — remontar é o único jeito
          de garantir que não sobre a regra da obra anterior no formulário. */}
      <RuleEditorOffcanvas
        key={workId}
        open={Boolean(editor)}
        onClose={() => setEditor(null)}
        project={activeProject}
        projectId={workId}
        readOnly={readOnly}
        initialTarget={editor?.target ?? null}
        initialRule={editor?.rule ?? null}
        profile={editor?.proposal?.profile ?? null}
        initialSource={editor?.proposal?.source ?? "manual"}
        rulesByRuleId={rulesByRuleId}
        onSaved={refresh}
      />

      {/* Mesmo motivo do `key` acima: nome, critério e insumos escolhidos são estado
          local e não podem atravessar uma troca de obra. */}
      <GroupEditorOffcanvas
        key={`group-editor-${workId}`}
        open={Boolean(groupEditor)}
        onClose={() => setGroupEditor(null)}
        project={activeProject}
        projectId={workId}
        readOnly={readOnly}
        initialGroup={groupEditor?.group ?? null}
        onSaved={refresh}
      />

      {/* Só existe montado enquanto aberto: fechar/reabrir sempre pede um rascunho
          novo, e nunca sobrevive a uma troca de obra (efeito acima zera o estado). */}
      {organizeOpen ? (
        <OrganizeWithAiPanel
          workId={workId}
          onApplied={(result) => {
            setOrganizeOpen(false);
            refresh(result);
          }}
          onClose={() => setOrganizeOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default Disbursement;
